import { useState, useEffect, useContext, useRef } from 'react';
import { AnimatePresence, motion } from "framer-motion";
import Questions from '../components/Questions';
import { CONFIG } from "../config";
import { isDebugMode } from '../utils/utils';

const Address = ({
  subGroup,
  validationErrors,
  setValidationErrors,
  inputRefs,
  handleInputBlur,
  language,
  setQuestions,
  firstLevelArea,
  secondLevelArea,
  subArea,
  inputBlurTriggered,
  savingQuestionId,
  fetchAreaNames,
  client,
  product,
  subIndex,
  setIsDataSaved
}) => {


  const posteCanadaKey = CONFIG.REACT_APP_POSTECANADAKEY;
  const [pcaReady, setPcaReady] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);  // Key to force re-render

  // State for the customize question
  const [customizeQuestion, setCustomizeQuestion] = useState(null);

  useEffect(() => {
    const foundQuestion = subGroup.subAreaCompositionDtoList.find(
      q => q.uiRenderType === "ADDRESS_CUSTOMIZE"
    );
    setCustomizeQuestion(foundQuestion);
    // Initialize edit mode based on the question's value if it exists
    if (foundQuestion && foundQuestion.value !== null) {
      setEditMode(foundQuestion.value === true);
    }
  }, [subGroup]);

  //const getQuestionByCode = (code) => {
  //   return subGroup.subAreaCompositionDtoList.find(question => question.code === code);
  //  };

  const getQuestionByCodes = (codes) => {
    return subGroup.subAreaCompositionDtoList.find(question => codes.includes(question.code));
  };

  const forceRefresh = () => {
    setRefreshKey(oldKey => oldKey + 1);  // Increment key to force re-render
  };

  const provinceToNumber = {
    'AB': 101,
    'BC': 102,
    'MB': 103,
    'NB': 104,
    'NL': 105,
    'NS': 107,
    'ON': 108,
    'PE': 109,
    'QC': 110,
    'SK': 111,
    'NT': 106,
    'NU': 113,
    'YT': 112
  };


  const SEARCH_ADDRESS_CODES = [
    "Q_SEARCH_ADDRESS_CIVIL",
    "Q_OWNER_SEARCH_ADDRESS_CIVIL",
    "Q_OWNER_SEARCH_ADDRESS_COMP"
  ];

  const ADDRESS_CODES = [
    "Q_ADDRESS",
    "Q_OWNER_ADDRESS",
    "Q_OWNER_ADDRESS_COMP"
  ];

  const APPARTMENT_CODES = [
    "Q_APPARTMENT",
    "Q_OWNER_APPARTMENT",
    "Q_OWNER_SUITE_COMP"
  ];

  const CITY_CODES = [
    "Q_CITY",
    "Q_OWNER_CITY_PERSON",
    "Q_OWNER_CITY_COMPANY"
  ];

  const PROVINCE_CODES = [
    "Q_PROVINCE",
    "Q_OWNER_PROVINCE_PERSON",
    "Q_OWNER_PROVINCE_COMPANY"
  ];

  const POSTAL_CODES_CODES = [
    "Q_POSTAL_CODE",
    "Q_OWNER_POSTAL_CODE_PERSON",
    "Q_OWNER_POSTAL_CODE_COMPANY"
  ];




  useEffect(() => {
    const scriptId = "scriptCAPostID";
    const styleId = "stylesheetCAPostID";

    const loadScript = () => {
      if (document.getElementById(scriptId)) {
        return Promise.resolve();
      }
      return new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.async = true;
        script.id = scriptId;
        script.src = "https://ws1.postescanada-canadapost.ca/js/addresscomplete-2.30.min.js";
        script.onload = () => {
          // console.log("Canada Post script loaded successfully.");
          resolve();
        };
        script.onerror = () => {
          console.error("Failed to load Canada Post script.");
          reject();
        };
        document.head.appendChild(script);
      });
    };

    const loadStyles = () => {
      if (document.getElementById(styleId)) {
        return Promise.resolve();
      }
      return new Promise((resolve, reject) => {
        const link = document.createElement("link");
        link.async = true;
        link.id = styleId;
        link.rel = "stylesheet";
        link.type = "text/css";
        link.href = "https://ws1.postescanada-canadapost.ca/css/addresscomplete-2.30.min.css";
        link.onload = () => {
          //console.log("Canada Post styles loaded successfully.");
          resolve();
        };
        link.onerror = () => {
          console.error("Failed to load Canada Post styles.");
          reject();
        };
        document.head.appendChild(link);
      });
    };

    Promise.all([loadScript(), loadStyles()])
      .then(() => setPcaReady(true))
      .catch((error) => {
        console.error("Failed to load Canada Post resources", error);
        alert("Failed to load Canada Post resources");
      });
  }, []);

  useEffect(() => {
    if (pcaReady && window.pca) {
      const firstInputQuestion = getQuestionByCodes(SEARCH_ADDRESS_CODES);
      const firstInputId = firstInputQuestion ? firstInputQuestion.uniqueId : null;
      const searchInput = firstInputId ? document.getElementById(firstInputId) : null;

      if (searchInput) {
        const fields = [
          { element: 'country-code', field: 'CountryIso2', mode: window.pca.fieldMode.DEFAULT },
          { element: searchInput.id, field: 'Line1', mode: window.pca.fieldMode.DEFAULT },
          { element: 'get-address', field: 'Line1', mode: window.pca.fieldMode.POPULATE },
          { element: 'get-city', field: 'City', mode: window.pca.fieldMode.POPULATE },
          { element: 'get-province', field: 'Province', mode: window.pca.fieldMode.POPULATE },
          { element: 'get-postcode', field: 'PostalCode', mode: window.pca.fieldMode.POPULATE },
        ];
        const options = {
          key: posteCanadaKey,
          countries: ['CA'],
          culture: language === 'fr' ? 'fr-CA' : 'en-CA'
        }
        new window.pca.Address(fields, options);
      } else {
        // console.warn("Search input is not available; skipping PCA initialization.");
      }
    }
  }, [pcaReady, refreshKey, subGroup]);


  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `  
    .pcafooter {
      background-color: aqua;
      display: none;
    }
    .pca .pcaautocomplete {
      border-width: 0;
    }`;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const addressInputRef = useRef(null);
  const cityInputRef = useRef(null);
  const provinceInputRef = useRef(null);
  const postcodeInputRef = useRef(null);

  const [currentValues, setCurrentValues] = useState({
    address: '',
    city: '',
    province: '',
    postcode: ''
  });




useEffect(() => {
  const checkValueChange = async () => {
    //check for refs
    if (!addressInputRef.current || !cityInputRef.current || !provinceInputRef.current || !postcodeInputRef.current) {
      return; // refs not ready yet
    }

    const newValues = {
      address: addressInputRef.current.value,
      city: cityInputRef.current.value,
      province: provinceInputRef.current.value,
      postcode: postcodeInputRef.current.value
    };

    const allFieldsFilled =
      newValues.address && newValues.city && newValues.province && newValues.postcode;

    if (JSON.stringify(newValues) !== JSON.stringify(currentValues) && allFieldsFilled) {
      setCurrentValues(newValues);
      setEditMode(false); // Disable edit mode when address is selected

      // Update the customize question value to 0 when address is selected
      const updatedQuestions = [
        { ...getQuestionByCodes(SEARCH_ADDRESS_CODES), value: null },
        { ...getQuestionByCodes(ADDRESS_CODES), value: newValues.address },
        { ...getQuestionByCodes(CITY_CODES), value: newValues.city },
        {
          ...getQuestionByCodes(PROVINCE_CODES),
          value: provinceToNumber[newValues.province] || '1'
        },
        { ...getQuestionByCodes(POSTAL_CODES_CODES), value: newValues.postcode }
      ];

      if (customizeQuestion) {
        updatedQuestions.push({
          ...customizeQuestion,
          value: false
        });
      }

      await handleInputBlur(updatedQuestions);
      forceRefresh();
    }
  };

  const intervalId = setInterval(checkValueChange, 100);
  return () => clearInterval(intervalId);
}, [currentValues, subGroup, handleInputBlur, customizeQuestion]);

  const toggleEditMode = (event) => {
    event.preventDefault();
    const newEditMode = !editMode;
    setEditMode(newEditMode);

    // Update the customize question value
    if (customizeQuestion) {
      const updatedQuestion = {
        ...customizeQuestion,
        value: newEditMode ? true : false
      };
      handleInputBlur([updatedQuestion]);
    }
  };


  const [searchAddressExists, setSearchAddressExists] = useState(false);

  useEffect(() => {
    const { subAreaCompositionDtoList } = subGroup;

    const isSearchAddress = subAreaCompositionDtoList[0].uiRenderType === "SEARCH_ADDRESS";


    setSearchAddressExists(isSearchAddress);

    subAreaCompositionDtoList.forEach((item, index) => {
      const inputElement = document.getElementById(item.uniqueId);
      if (inputElement) {
        // Disable if not in edit mode AND not the customize question
        const shouldDisable = !editMode && item.code !== "Q_ADDRESS_CUSTOMIZE";
        inputElement.disabled = shouldDisable;
      }
    });

    const firstInputElement = document.getElementById(
      getQuestionByCodes(SEARCH_ADDRESS_CODES)?.uniqueId
    );
    if (firstInputElement) {
      firstInputElement.disabled = !isSearchAddress && !editMode;
    }
  }, [editMode, inputBlurTriggered, subGroup, searchAddressExists]);

  return (
    <div key={`subGroup-${subIndex}`} className={subGroup.uiRenderType}>

      <input type="text" id="get-country" value="Canada" hidden readOnly />
      <input type="hidden" id="country-code" value="CA" readOnly />
      <input type="text" ref={addressInputRef} id="get-address" hidden />
      <input type="text" ref={cityInputRef} id="get-city" hidden />
      <input type="text" ref={provinceInputRef} id="get-province" hidden />
      <input type="text" ref={postcodeInputRef} id="get-postcode" hidden />

      {subGroup.name && <div className="title">{subGroup.name}</div>}


      <AnimatePresence mode="popLayout" initial={false}>
        {subGroup.subAreaCompositionDtoList
          .filter(question => question.uiRenderType !== "ADDRESS_CUSTOMIZE")
          .map((question, questionIndex) => {
            const sameRowQuestionsCount = question.sameRow
              ? subGroup.subAreaCompositionDtoList.filter(
                q => q.sameRow === question.sameRow
              ).length
              : 1;
            const questionWidth = question.sameRow
              ? `${100 / sameRowQuestionsCount}%`
              : "100%";

            return (
              <motion.div
                key={`question-${question.uniqueId}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className={
                  question.sameRow
                    ? `entry same-row`
                    : `entry ${question.compositionType} ${question.uniqueId}`
                }
                style={{ width: questionWidth, display: "inline-block" }}
              >
                <Questions
                  key={question.uniqueId}
                  question={question}
                  validationErrors={validationErrors}
                  setValidationErrors={setValidationErrors}
                  inputRefs={inputRefs}
                  setQuestions={setQuestions}
                  handleInputBlur={handleInputBlur}
                  firstLevelArea={firstLevelArea}
                  subArea={subArea}
                  secondLevelArea={secondLevelArea}
                  inputBlurTriggered={inputBlurTriggered}
                  savingQuestionId={savingQuestionId}
                  fetchAreaNames={fetchAreaNames}
                  client={client}
                  product={product}
                  language={language}
                  setIsDataSaved={setIsDataSaved}
                />
              </motion.div>
            );
          })}
      </AnimatePresence>




      {/* Custom UI for the ADDRESS_CUSTOMIZE type */}
      {customizeQuestion && customizeQuestion.uiRenderType === "ADDRESS_CUSTOMIZE" && (
        <div className="custom-links">
          <div
            className={`${validationErrors && validationErrors.some((error) => error.id === customizeQuestion.id)
              ? "hasError"
              : ""
              }`}
            ref={(el) => {
              const refId = customizeQuestion.uniqueId;
              inputRefs.current[refId] = el;
            }}

            data-question-code={customizeQuestion.code}

          >
            {isDebugMode() && <div className="dev-id">{customizeQuestion.uniqueId}</div>}


            <button
              type="button"
              className={`customize-button ${editMode ? "active" : ""}`}
              onClick={!editMode ? toggleEditMode : undefined} // Only allow click when not in edit mode
              disabled={editMode} // Disable the button when in edit mode
            >
              {customizeQuestion.text}
            </button>



            <div
              className={`invalid-feedback ${validationErrors &&
                validationErrors.some((error) => error.id === customizeQuestion.id) &&
                inputRefs.current[customizeQuestion.uniqueId]
                ? "d-block"
                : ""
                }`}
            >
              {validationErrors &&
                validationErrors.find((error) => error.id === customizeQuestion.id)?.errorText}
            </div>
          </div>
        </div>
      )}



    </div>
  );
};

export default Address;
